/**
 * Formats a phone number string with + and spaces
 * @param {string} phoneNumber - The phone number to format (e.g., "50512345678")
 * @returns {string} - Formatted phone number (e.g., "+505 1234 5678")
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber;
  
  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  
  if (digits.length === 0) return phoneNumber;
  
  // US numbers: +1 followed by 10 digits
  // Format: +1 XXX XXX XXXX
  if (digits.startsWith('1') && digits.length === 11) {
    return `+1 ${digits.substring(1, 4)} ${digits.substring(4, 7)} ${digits.substring(7)}`;
  }
  
  // Nicaragua (+505): 8 digits after country code
  // Format: +505 XXXX XXXX
  if (digits.startsWith('505') && digits.length === 11) {
    return `+505 ${digits.substring(3, 7)} ${digits.substring(7)}`;
  }
  
  // Central America countries with 3-digit codes (502, 503, 504, 506, 507): 8 digits after country code
  // Format: +XXX XXXX XXXX
  const centralAmericaCodes = ['502', '503', '504', '506', '507'];
  for (const code of centralAmericaCodes) {
    if (digits.startsWith(code) && digits.length === 11) {
      return `+${code} ${digits.substring(3, 7)} ${digits.substring(7)}`;
    }
  }
  
  // Generic formatting for other cases
  if (digits.length >= 8) {
    // Try to detect country code
    // If starts with common 1-digit codes (1), 2-digit codes, or 3-digit codes
    if (digits.length === 11 && digits.startsWith('1')) {
      // US format
      return `+1 ${digits.substring(1, 4)} ${digits.substring(4, 7)} ${digits.substring(7)}`;
    } else if (digits.length === 11 && digits.match(/^[2-9]\d{2}/)) {
      // Likely 3-digit country code (Central America, etc.)
      const countryCode = digits.substring(0, 3);
      const localNumber = digits.substring(3);
      return `+${countryCode} ${localNumber.substring(0, 4)} ${localNumber.substring(4)}`;
    } else if (digits.length === 10) {
      // No country code, format as local number
      return `+${digits.substring(0, 3)} ${digits.substring(3, 6)} ${digits.substring(6)}`;
    } else if (digits.length === 8) {
      // 8 digits, likely local number without country code
      return `+${digits.substring(0, 4)} ${digits.substring(4)}`;
    } else {
      // Fallback: format in groups
      let formatted = '+';
      if (digits.length > 8) {
        const countryCodeLength = digits.length - 8;
        formatted += digits.substring(0, countryCodeLength) + ' ';
        formatted += digits.substring(countryCodeLength, countryCodeLength + 4) + ' ';
        formatted += digits.substring(countryCodeLength + 4);
      } else {
        formatted += digits.substring(0, 4) + ' ';
        formatted += digits.substring(4);
      }
      return formatted;
    }
  }
  
  // For short numbers, just add + prefix
  return '+' + digits;
};

/**
 * Checks if a string is likely a phone number (only digits, possibly with +)
 * @param {string} str - String to check
 * @returns {boolean} - True if it looks like a phone number
 */
export const isPhoneNumber = (str) => {
  if (!str) return false;
  // Check if it's mostly digits (at least 8 digits)
  const digits = str.replace(/\D/g, '');
  return digits.length >= 8;
};

/**
 * Formats a contact name - if it's a phone number, format it, otherwise return as is
 * @param {string} name - Contact name or phone number
 * @returns {string} - Formatted name or phone number
 */
export const formatContactName = (name) => {
  if (!name) return name;
  // If it looks like a phone number, format it
  if (isPhoneNumber(name)) {
    return formatPhoneNumber(name);
  }
  return name;
};

